import { CalendarClock, ChevronDown, ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { z } from 'zod';

import { defineStandalone } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { UserTextMessage } from '@apps/web/features/phoebe_chat_v3/ui/primitives/chat_text_message';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { formatDateWithWeekday, formatTime } from '@apps/web/lib/date_utils';

// Automation kickoffs render as an event line; older runs keep the bubble.
const automationOriginSchema = z
    .object({
        kind: z.literal('automation'),
        automation_name: z.string(),
        fire_reason: z.enum(['schedule', 'event']),
        fired_at: z.iso.datetime({ offset: true }),
        fired_because: z.string().nullish(),
    })
    .strip();

const payloadSchema = z
    .object({
        text: z.string().max(100_000),
        display_only: z.boolean().optional(),
        automation_origin: automationOriginSchema.nullish(),
    })
    .strip();

type AutomationOrigin = z.infer<typeof automationOriginSchema>;

export const userMessageDefinitions = {
    user_message: defineStandalone('user_message', {
        disposition: 'render',
        schema: payloadSchema,
        malformed: {
            kind: 'placeholder',
            customerCopy: 'A message could not be displayed.',
        },
        evidence: null,
        group: null,
        render(payload, occurrence, context) {
            // A question answer's echo exists only to make the answer
            // durable; the resolved question card shows it.
            if (payload.display_only === true) {
                return null;
            }
            if (
                payload.automation_origin !== null &&
                payload.automation_origin !== undefined
            ) {
                return (
                    <AutomationKickoffLine
                        instructions={payload.text}
                        origin={payload.automation_origin}
                        organizationTimeZone={context.organizationTimeZone}
                    />
                );
            }
            return (
                <UserTextMessage
                    text={payload.text}
                    occurrence={occurrence}
                    organizationTimeZone={context.organizationTimeZone}
                />
            );
        },
    }),
} as const;

function AutomationKickoffLine({
    instructions,
    origin,
    organizationTimeZone,
}: Readonly<{
    readonly instructions: string;
    readonly origin: AutomationOrigin;
    readonly organizationTimeZone: string;
}>): ReactNode {
    const [instructionsVisible, setInstructionsVisible] = useState(false);
    const reason =
        origin.fire_reason === 'schedule'
            ? 'ran on schedule'
            : origin.fired_because
              ? `ran because ${origin.fired_because}`
              : 'ran on activity';
    return (
        <div>
            <EventLine glyph={<CalendarClock />}>
                <span className="font-semibold">{origin.automation_name}</span>{' '}
                {reason}
                {' · '}
                {formatDateWithWeekday(origin.fired_at, organizationTimeZone)}
                {', '}
                {formatTime(origin.fired_at, organizationTimeZone)}
            </EventLine>
            <EventLine
                glyph={instructionsVisible ? <ChevronDown /> : <ChevronRight />}
                className="mt-1.5"
                expanded={instructionsVisible}
                onClick={() => setInstructionsVisible(!instructionsVisible)}
            >
                {instructionsVisible
                    ? 'Hide instructions'
                    : 'Show instructions'}
            </EventLine>
            {instructionsVisible ? (
                <p className="text-muted-foreground text-v3-chat-meta mt-1.5 max-w-[66ch] pl-[57px] whitespace-pre-wrap">
                    {instructions}
                </p>
            ) : null}
        </div>
    );
}

export const userMessageContractFixtures = {
    valid: {
        type: 'user_message',
        text: 'Can you check this request?',
    },
    malformed: {
        type: 'user_message',
        text: 7,
    },
} as const;
